/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AgentArtifact, AgentEvent } from './types';

// Plot artifacts (plotly_html) are rendered inline as charts, never downloaded.
// Everything else with a download URL and filename is a downloadable file
// (CSV/Excel exports and any future file output).
const NON_DOWNLOADABLE_KINDS = new Set(['plotly_html', 'plot', 'land_parcel_map']);
const MAP_ARTIFACT_KINDS = new Set(['land_parcel_map']);

export const artifactFromEvent = (event: AgentEvent): AgentArtifact => ({
  id: event.artifact_id ?? crypto.randomUUID(),
  kind: event.artifact_kind,
  filename: event.filename,
  byteSize: event.byte_size,
  downloadUrl: event.download_url,
});

export const isDownloadableArtifact = (artifact: AgentArtifact): boolean =>
  Boolean(artifact.downloadUrl) &&
  Boolean(artifact.filename) &&
  !NON_DOWNLOADABLE_KINDS.has((artifact.kind ?? '').toLowerCase());

export const isLandParcelMapArtifact = (artifact: AgentArtifact): boolean =>
  MAP_ARTIFACT_KINDS.has((artifact.kind ?? '').toLowerCase());

export const downloadableArtifacts = (
  artifacts: Array<AgentArtifact> | undefined,
): Array<AgentArtifact> => (artifacts ?? []).filter(isDownloadableArtifact);

export const formatArtifactSize = (byteSize: number | null | undefined): string | undefined => {
  if (typeof byteSize !== 'number' || !Number.isFinite(byteSize) || byteSize < 0) return undefined;
  if (byteSize < 1024) return `${byteSize} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let size = byteSize / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || Number.isInteger(size) ? 0 : 1)} ${units[unitIndex]}`;
};

const SANDBOX_SCHEME = 'sandbox:';

/**
 * The agent sometimes emits artifact links with a `sandbox:` scheme (e.g.
 * `sandbox:/api/v1/proxy/agent/artifacts/.../file.xlsx`). That scheme is not a
 * real URL, so the browser can't open it — detect it and resolve it back to an
 * origin-relative path that goes through the authenticated agent proxy.
 */
export const isSandboxUrl = (href: string | undefined | null): href is string =>
  typeof href === 'string' && href.startsWith(SANDBOX_SCHEME);

export const sandboxUrlToPath = (href: string): string => {
  const path = href.slice(SANDBOX_SCHEME.length);
  return path.startsWith('/') ? path : `/${path}`;
};

export const artifactFilenameFromPath = (path: string): string => {
  const lastSegment = path.split('/').pop()?.split('?')[0] ?? '';
  try {
    return decodeURIComponent(lastSegment) || 'download';
  } catch {
    return lastSegment || 'download';
  }
};
